<template>
  <div class="support-page">
    <section class="resource-toolbar">
      <div>
        <h2>Suporte BOARUA</h2>
        <p>Abra tickets para problemas da plataforma, financeiro, app ou operação.</p>
      </div>
      <v-btn text color="primary" @click="openDialog()">
        <v-icon left size="18">mdi-plus</v-icon>
        Adicionar
      </v-btn>
    </section>

    <ListFilters
      v-model="filterDraft"
      :fields="filterFields"
      :loading="loading"
      @filter="applyFilters"
      @clear="applyFilters"
    />

    <section class="table-panel">
      <BoaruaDataTable :headers="headers" :items="filteredItems" :loading="loading" class="boarua-table">
        <template v-slot:item.association="{ item }">
          {{ item.association ? item.association.name : "-" }}
        </template>
        <template v-slot:item.created_at="{ item }">
          {{ item.created_at | defaultDate }}
        </template>
        <template v-slot:item.status="{ item }">
          <v-chip small color="secondary" text-color="primary">{{ statusLabel(item.status) }}</v-chip>
        </template>
        <template v-slot:item.priority="{ item }">
          {{ priorityLabel(item.priority) }}
        </template>
        <template v-slot:item.actions="{ item }">
          <v-btn icon small @click="openDialog(item)">
            <v-icon size="19">mdi-pencil-outline</v-icon>
          </v-btn>
        </template>
        <template v-slot:footer.prepend>
          <v-btn icon small :loading="loading" title="Atualizar" @click="load">
            <v-icon size="19">mdi-refresh</v-icon>
          </v-btn>
        </template>
      </BoaruaDataTable>
    </section>

    <v-dialog v-model="dialog" max-width="760">
      <v-card class="form-dialog">
        <v-card-title>
          <div>
            <strong>{{ form.id ? "Atualizar ticket" : "Novo ticket" }}</strong>
            <span>Informe os detalhes do atendimento da plataforma.</span>
          </div>
        </v-card-title>
        <v-card-text>
          <v-progress-linear v-if="loadingTicketDetails" indeterminate color="primary" class="mb-4" />
          <v-text-field v-model="form.subject" label="Assunto" outlined dense hide-details="auto" class="mb-3" />
          <v-row dense>
            <v-col cols="12" md="6">
              <v-select v-model="form.category" :items="categories" label="Categoria" outlined dense hide-details="auto" />
            </v-col>
            <v-col cols="12" md="6">
              <v-select v-model="form.priority" :items="priorityOptions" label="Prioridade" outlined dense hide-details="auto" />
            </v-col>
          </v-row>
          <v-textarea v-model="form.description" label="Descrição" outlined rows="4" hide-details="auto" class="mt-3" />
          <v-select v-if="form.id" v-model="form.status" :items="statusOptions" label="Status" outlined dense hide-details="auto" class="mt-3" />

          <section class="attachment-section">
            <div class="attachment-heading">
              <strong>Prints e comprovações</strong>
              <span>Arraste arquivos para anexar evidências do problema.</span>
            </div>
            <div
              class="dropzone"
              :class="{ active: draggingFiles }"
              @click="triggerFileInput"
              @dragenter.prevent="draggingFiles = true"
              @dragover.prevent="draggingFiles = true"
              @dragleave.prevent="draggingFiles = false"
              @drop.prevent="handleDrop"
            >
              <input ref="fileInput" type="file" multiple class="file-input" @change="handleFileSelect" />
              <v-icon size="28" color="primary">mdi-cloud-upload-outline</v-icon>
              <strong>Arraste e solte aqui</strong>
              <span>ou clique para selecionar imagens, PDFs e comprovantes</span>
            </div>
            <div v-if="attachmentFiles.length" class="attachment-list">
              <div v-for="(file, index) in attachmentFiles" :key="`${file.name}-${index}`" class="attachment-item">
                <v-icon size="18">mdi-paperclip</v-icon>
                <span>{{ file.name }}</span>
                <small>{{ fileSize(file.size) }}</small>
                <v-btn icon small @click="removeAttachment(index)">
                  <v-icon size="17">mdi-close</v-icon>
                </v-btn>
              </div>
            </div>
          </section>

          <section v-if="supportMessages.length" class="ticket-thread">
            <div class="attachment-heading">
              <strong>Histórico do atendimento</strong>
              <span>Mensagens e anexos enviados neste ticket.</span>
            </div>
            <div v-for="message in supportMessages" :key="message.id" class="thread-item">
              <div>
                <strong>{{ messageUserName(message) }}</strong>
                <span>{{ formatDateTime(message.created_at) }}</span>
              </div>
              <p v-if="message.message">{{ message.message }}</p>
              <a v-if="message.resource" :href="resourceUrl(message.resource)" target="_blank" rel="noopener" class="thread-attachment">
                <v-icon size="18">mdi-file-eye-outline</v-icon>
                {{ message.resource.name || "Anexo" }}
              </a>
            </div>
          </section>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialog = false">Cancelar</v-btn>
          <v-btn depressed color="primary" :loading="saving" @click="save">Salvar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";
import BoaruaDataTable from "@/views/boarua/components/BoaruaDataTable.vue";
import ListFilters from "@/views/boarua/components/ListFilters.vue";
import { applyListFilters, initialListFilters } from "@/views/boarua/helpers/listFilters";

export default {
  name: "BoaruaSupportTickets",
  components: { BoaruaDataTable, ListFilters },
  data() {
    return {
      loading: false,
      saving: false,
      dialog: false,
      draggingFiles: false,
      loadingTicketDetails: false,
      filterDraft: initialListFilters(),
      activeFilters: initialListFilters(),
      items: [],
      form: this.initialForm(),
      attachmentFiles: [],
      supportMessages: [],
      headers: [
        { text: "Assunto", value: "subject" },
        { text: "Categoria", value: "category" },
        { text: "Prioridade", value: "priority" },
        { text: "Status", value: "status" },
        { text: "Local", value: "association" },
        { text: "Criado em", value: "created_at" },
        { text: "", value: "actions", sortable: false, align: "end", width: 70 },
      ],
      categories: ["financeiro", "app", "painel", "pagamentos", "seguranca", "portaria", "outro"],
      priorities: ["low", "normal", "high", "urgent"],
      statuses: ["new", "in_progress", "waiting_customer", "waiting_team", "resolved", "closed"],
    };
  },
  computed: {
    filterFields() {
      return [
        { name: "status", label: "Status", type: "select", items: this.statusOptions },
        { name: "category", label: "Categoria", type: "select", items: this.categories.map((value) => ({ text: this.categoryLabel(value), value })) },
        { name: "priority", label: "Prioridade", type: "select", items: this.priorityOptions },
      ];
    },
    priorityOptions() {
      return this.priorities.map((value) => ({ text: this.priorityLabel(value), value }));
    },
    statusOptions() {
      return this.statuses.map((value) => ({ text: this.statusLabel(value), value }));
    },
    filteredItems() {
      return applyListFilters(this.items, this.activeFilters, {
        textKeys: ["subject", "category", "priority", "status", "description"],
        dateKeys: ["created_at", "updated_at"],
      });
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    initialForm() {
      return { subject: "", category: "painel", priority: "normal", status: "new", description: "" };
    },
    applyFilters(filters) {
      this.activeFilters = Object.assign(initialListFilters(), filters || {});
    },
    statusLabel(value) {
      const labels = {
        new: "Novo",
        in_progress: "Em andamento",
        waiting_customer: "Aguardando cliente",
        waiting_team: "Aguardando equipe",
        resolved: "Resolvido",
        closed: "Fechado",
      };
      return labels[value] || value;
    },
    categoryLabel(value) {
      const labels = {
        financeiro: "Financeiro",
        app: "App",
        painel: "Painel",
        asaas: "Pagamentos",
        pagamentos: "Pagamentos",
        seguranca: "Segurança",
        portaria: "Portaria",
        outro: "Outro",
      };
      return labels[value] || value;
    },
    priorityLabel(value) {
      const labels = { low: "Baixa", normal: "Normal", high: "Alta", urgent: "Urgente" };
      return labels[value] || value;
    },
    async load() {
      this.loading = true;
      try {
        const response = await axios.get("/support-tickets", { params: { limit: 100 } });
        this.items = response.data.content.data || response.data.content || [];
      } finally {
        this.loading = false;
      }
    },
    async openDialog(item = null) {
      this.form = item ? Object.assign({}, item) : this.initialForm();
      this.attachmentFiles = [];
      this.supportMessages = this.normalizeMessages(item && item.messages);
      this.dialog = true;

      if (item && item.id) {
        await this.loadTicketDetails(item.id);
      }
    },
    async loadTicketDetails(id) {
      this.loadingTicketDetails = true;
      try {
        const response = await axios.get(`/support-tickets/${id}`);
        const ticket = response.data.content || {};
        this.form = Object.assign({}, this.form, ticket);
        this.supportMessages = this.normalizeMessages(ticket.messages);
      } catch (error) {
        this.notifyError("Não foi possível carregar os anexos do ticket");
      } finally {
        this.loadingTicketDetails = false;
      }
    },
    async save() {
      this.saving = true;
      try {
        let ticketId = this.form.id;
        if (this.form.id) {
          await axios.patch(`/support-tickets/${this.form.id}`, this.form);
        } else {
          const response = await axios.post("/support-tickets", this.form);
          const created = response.data.content || {};
          ticketId = created.id;
        }
        if (ticketId) {
          await this.uploadAttachments(ticketId);
        }
        this.dialog = false;
        await this.load();
        this.notifySuccess("Ticket salvo");
      } catch (error) {
        this.notifyError("Não foi possível salvar o ticket");
      } finally {
        this.saving = false;
      }
    },
    async uploadAttachments(ticketId) {
      for (const file of this.attachmentFiles) {
        const resourceId = await this.uploadResource(file);
        await axios.post(`/support-tickets/${ticketId}/messages`, {
          message: `Anexo enviado: ${file.name}`,
          resource_id: resourceId,
        });
      }
      this.attachmentFiles = [];
    },
    async uploadResource(file) {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("name", file.name || "Anexo do suporte");
      const response = await axios.post("/resources", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data.content && response.data.content.id;
    },
    triggerFileInput() {
      if (this.$refs.fileInput) this.$refs.fileInput.click();
    },
    handleFileSelect(event) {
      this.addFiles(event.target.files);
      event.target.value = "";
    },
    handleDrop(event) {
      this.draggingFiles = false;
      this.addFiles(event.dataTransfer.files);
    },
    addFiles(fileList) {
      const incoming = Array.from(fileList || []);
      incoming.forEach((file) => {
        const duplicate = this.attachmentFiles.some((current) => current.name === file.name && current.size === file.size);
        if (!duplicate) this.attachmentFiles.push(file);
      });
    },
    removeAttachment(index) {
      this.attachmentFiles.splice(index, 1);
    },
    normalizeMessages(messages) {
      if (!messages) return [];
      if (Array.isArray(messages)) return messages;
      if (Array.isArray(messages.rows)) return messages.rows;
      if (Array.isArray(messages.data)) return messages.data;
      return [];
    },
    messageUserName(message = {}) {
      const user = message.user || {};
      return user.name || "Usuário";
    },
    resourceUrl(resource = {}) {
      return resource.file_url || resource.url || "#";
    },
    fileSize(size) {
      const value = Number(size || 0);
      if (!value) return "";
      if (value < 1024 * 1024) return `${Math.ceil(value / 1024)} KB`;
      return `${(value / (1024 * 1024)).toFixed(1)} MB`;
    },
    formatDateTime(value) {
      if (!value) return "-";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "-";
      return date.toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    notifySuccess(message) {
      this.$dialog && this.$dialog.notify && this.$dialog.notify.success(message, { position: "top-right", timeout: 3000 });
    },
    notifyError(message) {
      this.$dialog && this.$dialog.notify && this.$dialog.notify.error(message, { position: "top-right", timeout: 4000 });
    },
  },
};
</script>

<style scoped lang="scss">
.resource-toolbar {
  margin-bottom: 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;

  h2,
  p {
    margin: 0;
  }

  h2 {
    color: #111827;
    font-size: 20px;
  }

  p {
    margin-top: 3px;
    color: #66736b;
    font-size: 13px;
  }
}

.table-panel {
  padding: 18px;
  background: #ffffff;
  border: 1px solid #e1e7e0;
  border-radius: 8px;
}

.boarua-table::v-deep th {
  color: #66736b !important;
  font-size: 12px !important;
  text-transform: uppercase;
}

.attachment-section,
.ticket-thread {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #dce8e2;
}

.attachment-heading {
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin-bottom: 10px;

  strong {
    color: #102347;
    font-size: 15px;
  }

  span {
    color: #66736b;
    font-size: 13px;
  }
}

.dropzone {
  min-height: 118px;
  padding: 18px;
  border: 1px dashed #8ebead;
  border-radius: 8px;
  background: #f6fbf8;
  color: #173154;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease;

  &.active {
    border-color: #007f3d;
    background: #e9f7ef;
  }

  strong {
    font-size: 15px;
  }

  span {
    color: #66736b;
    font-size: 13px;
  }
}

.file-input {
  display: none;
}

.attachment-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 10px;
}

.attachment-item,
.thread-item {
  border: 1px solid #dce8e2;
  border-radius: 8px;
  background: #ffffff;
}

.attachment-item {
  min-height: 42px;
  padding: 8px 10px;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto auto;
  align-items: center;
  gap: 8px;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #173154;
  }

  small {
    color: #66736b;
  }
}

.ticket-thread {
  display: flex;
  flex-direction: column;
}

.thread-item {
  padding: 12px;
  margin-top: 8px;

  > div {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
  }

  strong {
    color: #102347;
  }

  span {
    color: #66736b;
    font-size: 12px;
  }

  p {
    margin: 8px 0 0;
    color: #173154;
  }
}

.thread-attachment {
  margin-top: 10px;
  min-height: 36px;
  padding: 8px 10px;
  border-radius: 7px;
  background: #eef8f3;
  color: #007f3d;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-weight: 700;
  text-decoration: none;
}
</style>
